import { PatientId, WithPatientId } from '../surgicalEvent.slice';
import { doctorBffApi } from '@/modules';
import { PremedicationService } from './premedication.service';
import { PremedicationOrder } from './premedication.model';
import { wrapError } from '../../error';
import { ID } from '@ianesthesie-kit/services';

export const premedicationApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    premedications: builder.query<PremedicationOrder[], PatientId>({
      queryFn: ({ patientId }) =>
        PremedicationService.getInstance(patientId).fetch().catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'Premedication', id: patientId }
      ]
    }),

    upsertPremedication: builder.mutation<
      PremedicationOrder[],
      WithPatientId<{ order: Partial<PremedicationOrder> }>
    >({
      queryFn: ({ patientId, order }) =>
        PremedicationService.getInstance(patientId)
          .upsert(order)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) => [
        'Dashboard',
        { type: 'Premedication', id: patientId },
        // upserting clears the "no premedication" flag server-side
        { type: 'ActiveSurgicalEvent', id: patientId }
      ]
    }),

    deletePremedication: builder.mutation<
      PremedicationOrder[],
      WithPatientId<ID, 'orderId'>
    >({
      queryFn: ({ patientId, orderId }) =>
        PremedicationService.getInstance(patientId)
          .delete(orderId)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) => [
        'Dashboard',
        { type: 'Premedication', id: patientId }
      ]
    }),

    setNoPremedication: builder.mutation<void, WithPatientId<boolean, 'value'>>(
      {
        queryFn: ({ patientId, value }) =>
          PremedicationService.getInstance(patientId)
            .setNoPremedication(value)
            .then(() => ({ data: {} as any }))
            .catch(wrapError),
        invalidatesTags: (result, error, { patientId }) =>
          !error
            ? [
                { type: 'ActiveSurgicalEvent', id: patientId },
                { type: 'Premedication', id: patientId }
              ]
            : []
      }
    ),

    importPremedicationFromProtocol: builder.mutation<
      PremedicationOrder[],
      WithPatientId<ID, 'protocolId'>
    >({
      queryFn: ({ patientId, protocolId }) =>
        PremedicationService.getInstance(patientId)
          .importFromProtocol(protocolId)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) => [
        'Dashboard',
        { type: 'Premedication', id: patientId },
        { type: 'ActiveSurgicalEvent', id: patientId }
      ]
    })
  }),
  overrideExisting: false
});

export const {
  usePremedicationsQuery,
  useUpsertPremedicationMutation,
  useDeletePremedicationMutation,
  useSetNoPremedicationMutation,
  useImportPremedicationFromProtocolMutation
} = premedicationApi;
